import { Ray } from "@babylonjs/core/Culling/ray";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { TransformElements } from "../../core/cmd/TransformElements";
import { BoundingBox } from "../../core/mdl/BoundingBox";
import { AbstractTool } from "../../core/tool/AbstractTool";
import { InteractionMode } from "../../core/tool/Tool";
import { intersectRayPlane, VECTOR3_X, VECTOR3_Y, VECTOR3_Z } from "../../core/utils/MathUtils";
import { CameraMode } from "../../core/view/CameraControl";
import { CoordinatesType } from "../../core/view/View";
import { Log } from "../../utils";
import { clamp, eq } from "../../utils/math";
export class AbstractMove extends AbstractTool {
    constructor(pApp) {
        super(pApp);
        this.mView = this.mApp.viewer.view;
        this.mPickPlane = new Plane(0, 1, 0, 0);
        this.mRefPoint = Vector3.Zero();
        this.mTotalTrans = Vector3.Zero();
        this.mPickOffset = Vector3.Zero();
        this.mBBoxWorld = new BoundingBox();
        this.mLastPickPos = null;
        this.mCommand = null;
        this.mIsFinished = false;
        this.mMoveY = false;
        this.mAllowMoveBelowGround = true;
        this.mGroundPosition = 0;
        this.mMoveLimitMin = new Vector3(-20, -20, -20);
        this.mMoveLimitMax = new Vector3(20, 20, 20);
    }
    start(pParameter) {
        this.mIsFinished = false;
        this.mLastPickPos = null;
        this.mTotalTrans = Vector3.Zero();
        this.prepareSnapping();
        this.mCommand = new TransformElements(this.mApp, this.mApp.model.selection);
        this.setInteractionMode(InteractionMode.Primary);
        return (super.start(pParameter));
    }
    stop() {
        if (!this.mIsFinished && this.mCommand != null)
            this.mCommand.undoTransforms();
        this.mCommand = null;
        super.stop();
    }
    handlePointerUp(pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent))
            return;
        this.mIsFinished = true;
        if (eq(this.mTotalTrans.length(), 0) || this.mCommand == null) {
            this.stop();
        }
        else {
            this.mApp.commands.executeCommand(this.mCommand).then((pExecuted) => {
                this.stop();
            }).catch((pError) => {
                Log.error("Failed to execute TransformElements command.", pError);
            });
        }
    }
    handlePointerMove(pEvent, pState) {
        if (this.mCommand == null)
            return;
        const tPointerPos = this.getPointerPosition(pEvent, pState, false);
        if (tPointerPos == null)
            return;
        if (this.mLastPickPos == null) {
            const tInitialPick = this.getPlanePickPos(this.getPointerPosition(pEvent, pState, true));
            if (tInitialPick != null) {
                this.mLastPickPos = this.mRefPoint;
                this.mPickOffset = this.mRefPoint.subtract(tInitialPick);
            }
            else
                return;
        }
        let tCurrentPos = this.getPlanePickPos(tPointerPos);
        if (tCurrentPos == null)
            return;
        tCurrentPos.addInPlace(this.mPickOffset);
        tCurrentPos = this.performSnap(tCurrentPos, pEvent, pState);
        if (tCurrentPos == null)
            return;
        const tDiff = tCurrentPos.subtract(this.mLastPickPos);
        if (this.mMoveY) {
            tDiff.x = 0;
            tDiff.z = 0;
        }
        this.mTotalTrans.addInPlace(tDiff);
        this.mCommand.translate(tDiff);
        this.mLastPickPos = tCurrentPos;
    }
    setMoveLimit(pMin, pMax) {
        this.mMoveLimitMin = pMin.clone();
        this.mMoveLimitMax = pMax.clone();
    }
    getPickPlane(pRefPoint) {
        const tCamMode = this.mView.cameraControl.cameraMode;
        if (tCamMode === CameraMode.Orthographic ||
            tCamMode === CameraMode.Perspective) {
            let tNormal = null;
            if (this.mMoveY) {
                const tCamDir = this.mView.cameraControl.direction;
                tNormal = VECTOR3_X;
                const tDot = Math.abs(Vector3.Dot(tCamDir, tNormal));
                const tNewNormal = VECTOR3_Z;
                const tNewDot = Math.abs(Vector3.Dot(tCamDir, tNewNormal));
                if (tNewDot > tDot)
                    tNormal = tNewNormal;
            }
            else {
                tNormal = VECTOR3_Y;
                //switch from XZ plane to plane defined by camera for snapped side views
                if (tCamMode === CameraMode.Orthographic &&
                    this.mView.cameraControl.orthoOrbitSnappingEnabled) {
                    const tCamDir = this.mView.cameraControl.direction;
                    const tDot = Vector3.Dot(tCamDir, tNormal);
                    if (eq(tDot, 0.0)) {
                        tNormal = tCamDir;
                    }
                }
            }
            return Plane.FromPositionAndNormal(pRefPoint, tNormal);
        }
        else {
            return Plane.FromPositionAndNormal(pRefPoint, this.mView.cameraControl.direction);
        }
    }
    getPointerPosition(pEvent, pState, pInitialPos) {
        if (!pState.isPrimaryPointer(pEvent))
            return (null);
        const tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return (null);
        return (pInitialPos ? tPointer.initialPosition : tPointer.position);
    }
    getPlanePickPos(pPos) {
        if (pPos != null && this.mPickPlane != null) {
            this.mView.createPickingRayToRef(pPos, CoordinatesType.Client, null, gTmpRay);
            return (intersectRayPlane(gTmpRay, this.mPickPlane));
        }
        return (null);
    }
    performSnap(pPos, pEvent, pState) {
        return (pPos);
    }
    preventMoveBelowGround(pPos) {
        if (this.mAllowMoveBelowGround)
            return (pPos);
        if (pPos == null || this.mLastPickPos == null)
            return (null);
        const tCurrentTrans = pPos.subtract(this.mLastPickPos);
        const tPoint = this.mBBoxWorld.min.add(this.mTotalTrans);
        tPoint.addInPlace(tCurrentTrans);
        if (tPoint.y < this.mGroundPosition) {
            const tNewPos = pPos.clone();
            tNewPos.y += this.mGroundPosition - tPoint.y;
            return (tNewPos);
        }
        else
            return (pPos);
    }
    limitMovement(pPos) {
        if (pPos == null)
            return (null);
        const tNewPos = pPos.clone();
        tNewPos.x = clamp(tNewPos.x, this.mMoveLimitMin.x, this.mMoveLimitMax.x);
        tNewPos.y = clamp(tNewPos.y, this.mMoveLimitMin.y, this.mMoveLimitMax.y);
        tNewPos.z = clamp(tNewPos.z, this.mMoveLimitMin.z, this.mMoveLimitMax.z);
        return (tNewPos);
    }
}
//// temp variables ////////////////////////////////////////////////////////////////////////////
const gTmpRay = Ray.Zero();
